import { defaultGuideTitle, importPrompt, parseImport, type ImportError } from '@nolens/domain';
import { Banner, Button, Dialog, Field, Textarea, useLocale, useToast } from '@nolens/ui';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { importText } from '../i18n/importGuide';
import { ApiError } from '../lib/api';
import { useImportGuide } from '../lib/queries';

/**
 * Importar um guia escrito por uma IA (domain/import.ts). Escondido enquanto é testado: só o
 * proprietário da aplicação o vê. Para o abrir a toda a gente, IMPORT_FOR_ALL = true.
 */
export const IMPORT_FOR_ALL = false;

export const canImportGuide = (me: { isPlatformAdmin: boolean }) =>
  IMPORT_FOR_ALL || me.isPlatformAdmin;

function errorText(e: ImportError, t: ReturnType<typeof importText>): string {
  switch (e.code) {
    case 'json':
      return t.errJson;
    case 'format':
      return t.errFormat;
    case 'noSteps':
      return t.errNoSteps;
    case 'tooMany':
      return t.errTooMany;
    case 'noOpen':
      return t.errNoOpen;
    case 'step': {
      const why = {
        action: t.reasonAction,
        url: t.reasonUrl,
        element: t.reasonElement,
        option: t.reasonOption,
        text: t.reasonText,
      }[e.reason];
      return t.errStep(e.index + 1, why);
    }
  }
}

export function ImportGuideButton({ workspaceId }: { workspaceId: string }) {
  const locale = useLocale();
  const t = importText(locale);
  const toast = useToast();
  const navigate = useNavigate();
  const create = useImportGuide();
  const fileRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  const close = () => {
    setOpen(false);
    setError('');
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(importPrompt(locale));
      toast(t.copied, 'positive');
    } catch {
      toast(t.couldNotCopy, 'critical');
    }
  };

  const readFile = async (f: File | undefined) => {
    if (!f) return;
    setText(await f.text());
    setError('');
  };

  const submit = () => {
    const r = parseImport(text);
    if (!r.ok) {
      setError(errorText(r.error, t));
      return;
    }
    const g = r.guide;
    const language = g.language ?? locale;
    const id = crypto.randomUUID();
    create.mutate(
      {
        id,
        body: {
          workspaceId,
          title: g.title || defaultGuideTitle(g, language),
          description: g.description,
          language,
          steps: g.steps,
        },
      },
      {
        onSuccess: () => {
          close();
          setText('');
          navigate(`/g/${id}`);
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : t.failed),
      },
    );
  };

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        {t.import}
      </Button>
      <Dialog
        open={open}
        onClose={close}
        title={t.title}
        description={t.hint}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              {t.cancel}
            </Button>
            <Button onClick={submit} loading={create.isPending} disabled={!text.trim()}>
              {t.submit}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div>
            <Button variant="secondary" onClick={() => void copyPrompt()}>
              {t.copyPrompt}
            </Button>
          </div>
          <Field label={t.content} hint={t.contentHint}>
            {(p) => (
              <Textarea
                {...p}
                className="min-h-56 font-mono text-[12.5px]"
                spellCheck={false}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setError('');
                }}
              />
            )}
          </Field>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                void readFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <Button variant="ghost" onClick={() => fileRef.current?.click()}>
              {t.chooseFile}
            </Button>
          </div>
          {error && <Banner tone="critical">{error}</Banner>}
        </div>
      </Dialog>
    </>
  );
}
